import { jupiterFetch } from '@svs/nova-core/jupiter';

import type { Product } from '../../../@types/Product.js';
import {
  type DrawId,
  type DrawResponse,
  type ErrorResponse,
  isDrawResponse,
  isErrorResponse,
  isJackpotResponse,
  type JackpotResponse,
  type MultifetchResponse,
} from './types.js';

type CacheKey = `${Product}_${number}`;
export type ProductToTrack = {
  product: Product;
  drawNumber: number;
};

// const jackpotCache: Partial<Record<Product, string[]>> = {};
const jackpotCache = new Map<Product, string[]>();

// TODO: add correct draw api path and fix return type
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getSpecificDrawName = (product: Product): string => 'powerplay';

const jackpotProducts = new Set<Product>(['Stryktipset', 'Europatipset', 'Fulltraff', 'Maltipset']);

const getCacheKey = ({ product, drawNumber }: ProductToTrack): CacheKey => `${product}_${drawNumber}`;

export type LiteDraw = {
  regOpenTime: string;
  fund: {
    rolloverIn: string | undefined;
    extraMoney: string | undefined;
  };
};

const parseDraw = (draw: DrawResponse['draw']): LiteDraw => ({
  regOpenTime: draw.regOpenTime,
  fund: {
    rolloverIn: draw.fund?.rolloverIn.replace(',', '.'),
    extraMoney: draw.fund?.extraMoney.replace(',', '.'),
  },
});

// const drawInfoCache: Partial<Record<`${Product}_${number}`, ReturnType<typeof parseDraw>>> = {};
const drawInfoCache = new Map<`${Product}_${number}`, ReturnType<typeof parseDraw>>();
type FetchedDrawAndJackpot = { draws: Map<DrawId, LiteDraw>; jackpots: Map<Product, string[]> };

/**
 * Creates a multifetch request with the provided product information.
 * Fetches draw info like regOpenTime and jackpot (if the product has jackpot support).
 *
 * @param products - Array of objects containing product and draw number
 * @param jackpots - Array of objects containing product
 * @returns Returns an object containing a property for jackpots and draw info
 */
const fetchInfoForProducts = async (
  products: ProductToTrack[],
  jackpots: { product: Product }[]
): Promise<FetchedDrawAndJackpot> => {
  const drawsPaths = [];

  for (const { product, drawNumber } of products) {
    const productDrawName = getSpecificDrawName(product);
    const drawPath = [
      `/draw/1/${productDrawName}/draws/${drawNumber}?include=`,
      encodeURIComponent(['draw.fund.rolloverIn', 'draw.fund.extraMoney', `draw.regOpenTime`].join(',')),
    ].join('');

    drawsPaths.push(drawPath);
  }

  const jackpotPaths = [];
  for (const { product } of jackpots) {
    const productDrawName = getSpecificDrawName(product);
    const jackpotPath = [
      `/draw/1/jackpot/${productDrawName}?include=`,
      encodeURIComponent(['jackpot.jackpots.jackpotAmount', 'jackpot.guaranteedJackpots.jackpotAmount'].join(',')),
    ].join('');

    jackpotPaths.push(jackpotPath);
  }

  const mfSeparator = encodeURIComponent('|');
  const response = await jupiterFetch(
    `/multifetch?serial=false&urls=${[...drawsPaths, ...jackpotPaths].join(mfSeparator)}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    }
  );

  const data = (await response.json()) as MultifetchResponse<DrawResponse | JackpotResponse> | ErrorResponse;

  if (isErrorResponse(data)) {
    throw new Error(data.error.message ?? 'Error fetching product info');
  }
  if (!response.ok) {
    throw new Error('Error fetching product info');
  }

  const drawResponses = data.responses.slice(0, drawsPaths.length);
  const jackpotResponses = jackpotPaths.length > 0 ? data.responses.slice(-jackpotPaths.length) : [];

  const drawData = new Map<`${Product}_${number}`, ReturnType<typeof parseDraw>>();
  const jackpotData = new Map<Product, string[]>();

  // for (let i = 0; i < products.length; i++) {
  let index = 0;
  for (const { product, drawNumber } of products) {
    const key = getCacheKey({ product, drawNumber });
    const drawResponse = drawResponses[index];

    if (!drawResponse || isErrorResponse(drawResponse)) {
      continue;
    }

    if (!isDrawResponse(drawResponse)) {
      continue;
    }

    const parsedDrawData = parseDraw(drawResponse.draw);
    drawData.set(key, parsedDrawData);
    drawInfoCache.set(key, parsedDrawData);
    index++;
  }

  index = 0;
  for (const jackpotProduct of jackpots) {
    const { product } = jackpotProduct;
    const jackpotResponse = jackpotResponses[index];
    const jackpot: string[] = [];
    jackpotData.set(product, jackpot);

    if (!jackpotResponse || isErrorResponse(jackpotResponse)) {
      jackpotCache.set(product, jackpot);
      continue;
    }

    if (!isJackpotResponse(jackpotResponse)) {
      continue;
    }

    const jp = jackpotResponse.jackpot.jackpots.map(({ jackpotAmount }) => jackpotAmount.replace(',', '.'));
    const gjp = jackpotResponse.jackpot.guaranteedJackpots.map(({ jackpotAmount }) => jackpotAmount.replace(',', '.'));
    jackpotData.set(product, [...jp, ...gjp]);
  }

  return {
    draws: drawData,
    jackpots: jackpotData,
  };
};

/**
 * Fetches and caches (in memory) information about, jackpots, funds, draw close time etc
 * for the provided products.
 *
 * @param products - Array of objects containing product id and draw number
 * @returns Returns an object containing a property for jackpots and draw info
 */
export async function fetchAdditionalDrawInfo(products: ProductToTrack[]): Promise<FetchedDrawAndJackpot> {
  const productsToFetch = products.filter((product) => {
    const key = getCacheKey(product);
    return !drawInfoCache.get(key);
  });

  const jackpotsToFetch = products.filter(({ product }) => !jackpotCache.get(product) && jackpotProducts.has(product));

  if (productsToFetch.length > 0 || jackpotsToFetch.length > 0) {
    await fetchInfoForProducts(productsToFetch, jackpotsToFetch);
  }

  const retJackpot = new Map<Product, string[]>();
  const retDraw = new Map<DrawId, LiteDraw>();
  for (const productObj of products) {
    const { product } = productObj;
    if (jackpotCache.get(product)) {
      retJackpot.set(product, jackpotCache.get(product) ?? []);
    }

    const key = getCacheKey(productObj);
    const drawInfo = drawInfoCache.get(key);
    if (drawInfo) {
      retDraw.set(key, drawInfo);
    }
  }

  return {
    jackpots: retJackpot,
    draws: retDraw,
  } as const;
}

const clearCache = (): void => {
  jackpotCache.clear();
  drawInfoCache.clear();
};

fetchAdditionalDrawInfo.clearCache = clearCache;
